import {
  type ComponentProps,
  type FC,
  type RefObject,
  useState,
  useCallback,
  useEffect,
} from "react";
import { SectionTitle } from "@userFrontend/components/SectionTitle";
import { Label } from "@root/shared/components/Label";
import { Checkbox } from "@root/shared/components/Checkbox";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextArea } from "@root/shared/components/TextArea";
import {
  type SelectOptionQuestions,
  type ErrorText,
} from "@userFrontend/features/reward/components/ContinuedRewardInputs/type";
import { ErrorInformationText } from "@userFrontend/components/ErrorInformationText";
import { removeSpacesTabsNewlines } from "@root/shared/utils/removeSpacesTabsNewlines";
import { countCharacters } from "@root/shared/utils/countCharacters";
import * as styles from "./styles.css";

type Props = {
  // 質問
  questions: {
    rate: ComponentProps<typeof SelectBox>;
    reason: SelectOptionQuestions;
  };
  reasonCategoryName: string;
  // 選択された理由のIDの配列。「その他」の理由を選択した場合、TRUE。
  enableChooseRejobReasonText?: boolean;
  // 入力値
  satisfactionRateValue?: number;
  chooseRejobReasonValues: number[];
  chooseRejobReasonDetailValue?: string;
  thoughtsValue?: string;
  // event handlers
  handleSatisfactionRateChange: (satisfactionRateValue: number) => void;
  handleCheckboxChange: (categoryName: string, optionId: number) => void;
  handleTextAreaChange: (categoryName: string, value: string) => void;
  // error messages
  satisfactionErrorText: ErrorText;
  chooseRejobReasonDetailErrorText?: ErrorText;
  thoughtsErrorText?: ErrorText;
  // refs
  satisfactionRateRef: RefObject<HTMLDivElement>;
  chooseRejobReasonDetailRef: RefObject<HTMLDivElement>;
  thoughtsRef: RefObject<HTMLDivElement>;
};

export const Satisfaction: FC<Props> = ({
  questions,
  satisfactionRateValue,
  handleSatisfactionRateChange,
  satisfactionErrorText,
  reasonCategoryName,
  chooseRejobReasonValues,
  handleCheckboxChange,
  enableChooseRejobReasonText,
  chooseRejobReasonDetailValue,
  handleTextAreaChange,
  chooseRejobReasonDetailErrorText,
  thoughtsValue,
  thoughtsErrorText,
  satisfactionRateRef,
  chooseRejobReasonDetailRef,
  thoughtsRef,
}) => {
  // TODO: 汎用TextAreaWithWordCounterファイルは、「${文字数}以上か」のチェックであり、ここでは「${文字数}以内か」のチェックをしたいためベタがき。「以上」「以内」以外のスタイルや機能の統一を検討すること。
  // 感想の文字数カウント機能
  const [text, setText] = useState<string>(thoughtsValue || "");
  const [count, setCount] = useState<number>(() =>
    countCharacters(removeSpacesTabsNewlines(thoughtsValue || "")),
  );

  // 「その他」理由の文字数カウント機能
  const [reasonText, setReasonText] = useState<string>(chooseRejobReasonDetailValue || "");
  const [reasonCount, setReasonCount] = useState<number>(() =>
    countCharacters(removeSpacesTabsNewlines(chooseRejobReasonDetailValue || "")),
  );

  const handleChange = useCallback((value: string): void => {
    setText(value);
    setCount(countCharacters(removeSpacesTabsNewlines(value)));
  }, []);

  const handleReasonChange = useCallback((value: string): void => {
    setReasonText(value);
    setReasonCount(countCharacters(removeSpacesTabsNewlines(value)));
  }, []);

  const combinedChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(event.target.value);
    handleTextAreaChange("thoughts", event.target.value);
  };

  const combinedReasonChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleReasonChange(event.target.value);
    handleTextAreaChange("chooseRejobReasonDetail", event.target.value);
  };

  // valueが外部から変更された場合は以下のuseEffectで反映と再計算を行う
  useEffect(() => {
    handleChange(thoughtsValue || "");
  }, [handleChange, thoughtsValue]);

  // 「その他」理由のvalueが外部から変更された場合の再計算
  useEffect(() => {
    handleReasonChange(chooseRejobReasonDetailValue || "");
  }, [handleReasonChange, chooseRejobReasonDetailValue]);

  const thoughtsCounterMaxLength = 300;
  const reasonCounterMaxLength = 50;
  const isThoughtsOverLimit = count > thoughtsCounterMaxLength;
  const isReasonOverLimit = reasonCount > reasonCounterMaxLength;
  return (
    <div className={styles.outer}>
      <SectionTitle text="リジョブに対する満足度" />
      <div className={styles.inner}>
        <div ref={satisfactionRateRef} className={styles.section}>
          <p className={styles.textWithLabel}>
            満足度を5段階で評価してください
            <Label theme="red">必須</Label>
          </p>
          <div className={styles.selectBoxOuter}>
            <SelectBox
              {...questions.rate}
              value={satisfactionRateValue}
              selected={!!satisfactionRateValue}
              placeholder="選択してください"
              onChange={(event) => handleSatisfactionRateChange(Number(event?.target.value))}
              hasError={!!satisfactionErrorText}
            />
            {satisfactionErrorText && <ErrorInformationText text={satisfactionErrorText} />}
          </div>
        </div>
        <div className={styles.section}>
          <p className={styles.textWithLabel}>
            リジョブを利用した理由
            <Label theme="lightGray">任意</Label>
          </p>
          <div className={styles.checkboxesOuter}>
            {questions.reason.map(({ optionId, optionText }) => {
              const checked = chooseRejobReasonValues.includes(optionId) || false;
              return (
                <label key={optionId} className={styles.label} htmlFor={`${optionId}`}>
                  <Checkbox
                    name={reasonCategoryName}
                    checked={checked}
                    id={`${optionId}`}
                    onChange={() => handleCheckboxChange(reasonCategoryName, optionId)}
                  />
                  {optionText}
                </label>
              );
            })}
          </div>
          <div className={styles.reasonDetailOuter} ref={chooseRejobReasonDetailRef}>
            <TextArea
              className={styles.textArea}
              value={enableChooseRejobReasonText ? reasonText : ""}
              disabled={!enableChooseRejobReasonText}
              placeholder="「その他」を選択する場合、理由を記載してください。"
              onChange={combinedReasonChangeHandler}
              hasError={
                !!chooseRejobReasonDetailErrorText ||
                (enableChooseRejobReasonText && isReasonOverLimit)
              }
            />
            <div className={styles.counterText}>
              <p>
                <span
                  role="status"
                  aria-live="polite"
                  className={
                    enableChooseRejobReasonText && isReasonOverLimit ? styles.textOverLimit : ""
                  }
                >
                  {reasonCount}
                </span>
                /{reasonCounterMaxLength}文字以内
              </p>
            </div>
            {chooseRejobReasonDetailErrorText && (
              <ErrorInformationText text={chooseRejobReasonDetailErrorText} />
            )}
          </div>
        </div>
        <div className={styles.section} ref={thoughtsRef}>
          <p className={styles.textWithLabel}>
            感想
            <Label theme="lightGray">任意</Label>
          </p>
          <TextArea
            className={styles.textArea}
            value={text}
            placeholder="ご感想をご記入ください。"
            onChange={combinedChangeHandler}
            hasError={!!thoughtsErrorText || isThoughtsOverLimit}
          />
          <div className={styles.counterText}>
            <p>
              <span
                role="status"
                aria-live="polite"
                className={isThoughtsOverLimit ? styles.textOverLimit : ""}
              >
                {count}
              </span>
              /{thoughtsCounterMaxLength}文字以内
            </p>
          </div>
          {thoughtsErrorText && <ErrorInformationText text={thoughtsErrorText} />}
        </div>
      </div>
    </div>
  );
};
